import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  NewReleases as NewReleasesIcon,
  BugReport as BugReportIcon,
  GitHub as GitHubIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const ChangelogDialog = ({ open, onClose }) => {
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchChangelog();
    }
  }, [open]);

  const fetchChangelog = async () => {
    setLoading(true);
    try {
      const response = await fetch('/changelog.json');
      if (response.ok) {
        const data = await response.json();
        setChangelog(data);
      } else {
        setChangelog([]);
      }
    } catch (error) {
      console.error('Failed to fetch changelog:', error);
      setChangelog([]);
    } finally {
      setLoading(false);
    }
  };

  const getVersionTypeIcon = type => {
    return type === 'minor' ? (
      <NewReleasesIcon color='primary' />
    ) : (
      <BugReportIcon color='secondary' />
    );
  };

  const getVersionTypeColor = type => {
    return type === 'minor' ? 'primary' : 'secondary';
  };

  const formatCommitMessage = message => {
    // Clean up common commit prefixes
    return message
      .replace(/^(feat|fix|chore|docs|style|refactor|test):\s*/i, '')
      .replace(/^(feat|fix|chore|docs|style|refactor|test)\([^)]+\):\s*/i, '')
      .trim();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GitHubIcon color='primary' />
          <Typography variant='h6'>ShieldCRM Changelog</Typography>
        </Box>
        <IconButton onClick={onClose} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Loading changelog...</Typography>
          </Box>
        ) : changelog.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color='text.secondary'>No changelog entries found.</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {changelog.map((entry, index) => (
              <React.Fragment key={entry.version}>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch', p: 0 }}>
                  <Accordion sx={{ width: '100%', boxShadow: 'none' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        {getVersionTypeIcon(entry.type)}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant='h6' component='div'>
                            v{entry.version}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            {entry.summary}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label={entry.type}
                            color={getVersionTypeColor(entry.type)}
                            size='small'
                            variant='outlined'
                          />
                          <Typography variant='caption' color='text.secondary'>
                            {dayjs(entry.date).format('MMM D, YYYY')}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {dayjs(entry.date).fromNow()}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {entry.commits && entry.commits.length > 0 ? (
                        <Box>
                          <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>
                            Changes in this release:
                          </Typography>
                          <Paper variant='outlined' sx={{ p: 1, bgcolor: 'grey.50' }}>
                            {entry.commits.map(commit => (
                              <Box key={commit.hash} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                                <Typography
                                  variant='body2'
                                  sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                                >
                                  <Box
                                    component='span'
                                    sx={{ color: 'primary.main', fontWeight: 600 }}
                                  >
                                    {commit.hash}
                                  </Box>{' '}
                                  {formatCommitMessage(commit.message)}
                                </Typography>
                                {commit.date && (
                                  <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ ml: 1 }}
                                  >
                                    {dayjs(commit.date).format('MMM D, h:mm A')}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Paper>
                        </Box>
                      ) : (
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ fontStyle: 'italic' }}
                        >
                          No detailed commit information available for this release.
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </ListItem>
                {index < changelog.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant='outlined'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangelogDialog;
